import type { SavedPlan } from '@/api/savedPlans';

export function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeSelectionsJson(
  selections: unknown,
  context: string,
): Record<string, string | null> {
  if (Array.isArray(selections)) {
    const canonical: Record<string, string | null> = {};
    selections.forEach((row, index) => {
      if (!isObjectRecord(row)) {
        throw new Error(`${context}.selections_json[${index}] must be an object`);
      }
      const standId = row.stand_id;
      const branchId = row.branch_id;
      if (typeof standId !== 'string' || standId.length === 0) {
        throw new Error(`${context}.selections_json[${index}].stand_id must be a non-empty string`);
      }
      if (branchId !== null && (typeof branchId !== 'string' || branchId.length === 0)) {
        throw new Error(
          `${context}.selections_json[${index}].branch_id must be null or a non-empty string`,
        );
      }
      if (canonical[standId] !== undefined) {
        throw new Error(`${context}.selections_json contains duplicate stand_id ${standId}`);
      }
      canonical[standId] = branchId;
    });
    return canonical;
  }

  if (!isObjectRecord(selections)) {
    throw new Error(`${context}.selections_json must be an object`);
  }

  const canonical: Record<string, string | null> = {};
  for (const [standId, branchId] of Object.entries(selections)) {
    if (standId.length === 0) {
      throw new Error(`${context}.selections_json keys must be non-empty strings`);
    }
    if (branchId !== null && (typeof branchId !== 'string' || branchId.length === 0)) {
      throw new Error(`${context}.selections_json[${standId}] must be null or a non-empty string`);
    }
    canonical[standId] = branchId;
  }
  return canonical;
}

export function normalizeSavedPlan(raw: unknown, context = 'SavedPlan'): SavedPlan {
  if (!isObjectRecord(raw)) {
    throw new Error(`${context} must be an object`);
  }

  if (raw.payload_status === 'summary') {
    return {
      ...(raw as unknown as SavedPlan),
      owner_choice_stand_ids_json: Array.isArray(raw.owner_choice_stand_ids_json)
        ? raw.owner_choice_stand_ids_json.filter(
            (standId): standId is string => typeof standId === 'string' && standId.length > 0,
          )
        : [],
      selections_json: {},
      ops_json: null,
      stand_branches_json: null,
      stand_run_provenance_json: null,
    };
  }

  return {
    ...(raw as unknown as SavedPlan),
    owner_choice_stand_ids_json: Array.isArray(raw.owner_choice_stand_ids_json)
      ? raw.owner_choice_stand_ids_json.filter(
          (standId): standId is string => typeof standId === 'string' && standId.length > 0,
        )
      : [],
    selections_json: normalizeSelectionsJson(raw.selections_json, context),
  };
}

export function normalizeSavedPlans(raw: unknown, context = 'SavedPlan[]'): SavedPlan[] {
  const rows = isObjectRecord(raw) ? raw.results : raw;
  if (!Array.isArray(rows)) {
    throw new Error(`${context} must be an array`);
  }
  return rows.map((plan, index) => normalizeSavedPlan(plan, `${context}[${index}]`));
}

export function normalizeSavedPlanSummaries(raw: unknown): {
  rows: Array<Record<string, unknown>>;
  nextCursor: string | null;
} {
  if (!isObjectRecord(raw) || !Array.isArray(raw.results) || !isObjectRecord(raw.page)) {
    throw new Error('SavedPlan summary response must contain results and page');
  }
  const rows = raw.results.map((plan, index) => {
    if (!isObjectRecord(plan)) {
      throw new Error(`SavedPlan summary page[${index}] must be an object`);
    }
    if (typeof plan.id !== 'string' || plan.id.length === 0) {
      throw new Error(`SavedPlan summary page[${index}].id must be a string`);
    }
    if (plan.payload_status !== 'summary') {
      throw new Error(`SavedPlan summary page[${index}] must be metadata-only`);
    }
    return plan;
  });
  const nextCursor = raw.page.next_cursor;
  if (nextCursor !== null && (typeof nextCursor !== 'string' || nextCursor.length === 0)) {
    throw new Error('SavedPlan summary next_cursor must be null or a string');
  }
  return { rows, nextCursor };
}
